import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  DatePicker,
  Dropdown,
  Input,
  message,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MessageOutlined,
  MoreOutlined,
  PhoneOutlined,
  SwapOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLeads,
  updateLeadStatus,
  assignLead as assignLeadThunk,
  deleteLead as deleteLeadThunk,
  fetchLeadSource,
  fetchLeadPurpose,
} from "../leadThunk";
import { useNavigate, useSearchParams } from "react-router-dom";
import TaskDrawer from "../../TodayTasks/components/TaskDrawer";
import { fetchDeals } from "../../Deals/dealThunk";
import { fetchCrmFieldsApi, getLeadActivitiesApi, fetchMyTeammatesApi, bulkUpdateLeadsApi } from "../../../../apiservice/crm";
import LeadStatusSelect from "./LeadStatusSelect";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import { formatDayjsToUtc, formatToCompanyTimezone } from "../../../../lib/functions";
import { resolveDialableNumber, resolveWhatsAppNumber } from "../../../../lib/phone";

// Hovering the Activity Logs icon shows a quick-glance preview of the
// lead's most recent activity, instead of only opening the full modal on
// click (which it still does -- this is additive, not a replacement).
// Scoped to this one icon rather than the whole row so it can't fight the
// row's own click-to-navigate, the Agent dropdown, or the call/WhatsApp
// links for the mouse's attention. Fetched lazily on first hover and
// cached per row for the life of this table render, so sweeping the mouse
// down the list doesn't fire a request per row it merely passes over.
const ActivityPreviewPopover = ({ record, onViewActivity, children }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null); // null = not fetched yet

  const handleOpenChange = async (nextOpen) => {
    setOpen(nextOpen);

    if (nextOpen && items === null) {
      setLoading(true);
      try {
        const res = await getLeadActivitiesApi({ lead_id: record.id });
        setItems((res?.data || []).slice(0, 4));
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Popover
      trigger="hover"
      mouseEnterDelay={0.4}
      mouseLeaveDelay={0.2}
      open={open}
      onOpenChange={handleOpenChange}
      placement="left"
      overlayStyle={{ maxWidth: 320 }}
      content={
        <div onClick={(e) => e.stopPropagation()}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Recent Activity
          </div>

          {loading ? (
            <div style={{ padding: "12px 0", textAlign: "center" }}>
              <Spin size="small" />
            </div>
          ) : !items?.length ? (
            <div style={{ color: "#999", fontSize: 13, padding: "4px 0" }}>
              No activity yet
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    borderBottom: "1px solid #f0f0f0",
                    paddingBottom: 6,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <Tag style={{ margin: 0 }}>
                      {item.source === "task" ? "task" : item.type || "note"}
                    </Tag>
                    <span style={{ fontSize: 11, color: "#999" }}>
                      {item.created_at
                        ? formatToCompanyTimezone(item.created_at)
                        : ""}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      marginTop: 4,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {item.text || "-"}
                  </div>
                </div>
              ))}
            </div>
          )}

          <Button
            type="link"
            size="small"
            style={{ padding: 0, marginTop: 8 }}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onViewActivity?.(record);
            }}
          >
            View all
          </Button>
        </div>
      }
    >
      {children}
    </Popover>
  );
};

const LeadTable = ({
  list,
  tasks,
  loading,
  onAddActivity,
  onViewActivity,
  isAdmin,
  setEditDrawer,
  setSelectedLead,
  selectedLead,
  visibleColumnKeys = [],
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const users = useSelector((s) => s.lookups.users);
  const authInfo = useSelector(selectAuthInfo);
  const { list: deals } = useSelector((s) => s.deals);
  const sourceList = useSelector((s) => s.leads.sourceList);
  const purposeList = useSelector((s) => s.leads.purposeList);

  // For the bulk "Edit Fields" modal -- same fetch-if-empty convention
  // LeadStatusSelect.jsx already uses for statusList.
  useEffect(() => {
    if (!sourceList?.length) dispatch(fetchLeadSource());
    if (!purposeList?.length) dispatch(fetchLeadPurpose());
  }, [sourceList?.length, purposeList?.length, dispatch]);

  // Loaded once (not per-row) for the self-service "reassign my own lead to
  // a teammate" picker non-admins get in the Agent column below.
  const [myTeammates, setMyTeammates] = useState([]);
  useEffect(() => {
    if (isAdmin) return;
    fetchMyTeammatesApi()
      .then((res) => setMyTeammates(res.data?.members || []))
      .catch(() => setMyTeammates([]));
  }, [isAdmin]);

  // Bulk actions -- selection and its three quick-action pickers all live
  // locally here, same as myTeammates above, since this component owns its
  // own row state and the parent page just hands it `list`.
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkFieldsOpen, setBulkFieldsOpen] = useState(false);
  const [bulkValue, setBulkValue] = useState(null);
  const [bulkFields, setBulkFields] = useState({ source_id: undefined, purpose_id: undefined });
  const [bulkLoading, setBulkLoading] = useState(false);

  const bulkReassignOptions = isAdmin
    ? (users || []).map((u) => ({ label: u.full_name, value: u.id }))
    : myTeammates.map((u) => ({ label: u.full_name, value: u.id }));

  const runBulkUpdate = async (payload) => {
    setBulkLoading(true);
    try {
      const res = await bulkUpdateLeadsApi({ lead_ids: selectedRowKeys, ...payload });
      const { updated, failed, failures: failedRows } = res.data || {};

      if (updated) message.success(`${updated} lead${updated === 1 ? "" : "s"} updated`);

      if (failed) {
        Modal.warning({
          title: `${failed} lead${failed === 1 ? "" : "s"} couldn't be updated`,
          content: (
            <div style={{ maxHeight: 240, overflowY: "auto" }}>
              {(failedRows || []).map((f) => (
                <div key={f.leadId} style={{ marginBottom: 6 }}>{f.message}</div>
              ))}
            </div>
          ),
        });
      }

      setSelectedRowKeys([]);
      setBulkReassignOpen(false);
      setBulkStatusOpen(false);
      setBulkFieldsOpen(false);
      setBulkValue(null);
      setBulkFields({ source_id: undefined, purpose_id: undefined });
      dispatch(fetchLeads());
    } catch (err) {
      message.error(err?.response?.data?.message || "Bulk update failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const [convertModal, setConvertModal] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  // const [selectedLead, setSelectedLead] = useState(null);
  const [dealValue, setDealValue] = useState("");
  const [followUpDate, setFollowUpDate] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [taskDrawer, setTaskDrawer] = useState(false);
  // Page and page size live in the URL so that opening a lead and pressing
  // Back returns to the same page of the list.
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = {
    current: Number(searchParams.get("page")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || 10,
  };
  const filterState = useSelector((s) => s.leads.filterState);
  const filterKey = JSON.stringify(filterState);
  const filterSnapshot = useRef(filterKey);

  const setPagination = ({ current, pageSize }) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (current > 1) next.set("page", current);
        else next.delete("page");
        if (pageSize !== 10) next.set("pageSize", pageSize);
        else next.delete("pageSize");
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    const loadFields = async () => {
      try {
        const res = await fetchCrmFieldsApi({ module: "lead" });
        setCustomFields(res?.data || []);
      } catch (err) {
        console.log(err);
      }
    };
    loadFields();
  }, []);

  useEffect(() => {
    dispatch(fetchDeals());
  }, [dispatch]);

  // A new filter or search starts again from page 1. Ordinary reloads of the
  // same list (after an assign, a status change, or returning from a lead)
  // keep the current page.
  useEffect(() => {
    if (filterSnapshot.current === filterKey) return;
    filterSnapshot.current = filterKey;
    setPagination({ current: 1, pageSize: pagination.pageSize });
  }, [filterKey]);

  // If the list got shorter so the current page no longer exists, show the last page.
  useEffect(() => {
    if (!list?.length) return;
    const lastPage = Math.ceil(list.length / pagination.pageSize);
    if (pagination.current > lastPage) {
      setPagination({ current: lastPage, pageSize: pagination.pageSize });
    }
  }, [list, pagination.current, pagination.pageSize]);

  // ❌ REMOVE convert from here (only reject stays)
  const handleAction = async (record, action) => {
    try {
      await dispatch(
        updateLeadStatus({
          id: record.id,
          action,
          source: record.source,
        }),
      ).unwrap();

      message.success("Action completed ✅");
      dispatch(fetchLeads());
    } catch {
      message.error("Action failed ❌");
    }
  };

  // ✅ FINAL CONVERT FLOW
  const handleConvertSubmit = async () => {
    if (convertLoading) return;
    if (!dealValue) {
      return message.warning("Enter deal value");
    }

    if (!followUpDate) {
      return message.warning("Select follow-up date");
    }

    try {
      setConvertLoading(true);
      await dispatch(
        updateLeadStatus({
          id: selectedLead.id,
          action: "convert",
          // source: selectedLead.source,
          source: "account",
          dealValue,
          followUpDate:
            followUpDate
                ? formatDayjsToUtc(followUpDate)
                : null,
        }),
      ).unwrap();

      message.success("Converted → Deal & Task created 🚀");

      setConvertModal(false);
      setDealValue("");
      setFollowUpDate(null);

      navigate("/crm/deals");
    } catch {
      message.error("Conversion failed ❌");
    } finally {
      setConvertLoading(false); 
    }
  };

  const assignLeadHandler = async (leadId, userId) => {
    try {
      await dispatch(assignLeadThunk({ leadId, userId })).unwrap();
      message.success("Assigned successfully ✅");
      dispatch(fetchLeads());
    } catch {
      message.error("Assign failed ❌");
    }
  };

  const handleDeleteLead = (record) => {
    Modal.confirm({
      title: `Delete lead "${record.name}"?`,
      content: "This lead will be removed from all lists. This cannot be undone from here.",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispatch(deleteLeadThunk(record.id)).unwrap();
          message.success("Lead deleted");
          dispatch(fetchLeads());
        } catch (err) {
          message.error(err || "Failed to delete lead");
        }
      },
    });
  };

  const overdueTaskMap = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (t.bucket !== "overdue") return;

      map[t.lead_id] = (map[t.lead_id] || 0) + 1;
    });

    return map;
  }, [tasks]);

  const taskIndicatorMap = useMemo(() => {
    const map = {};

    tasks.forEach((task) => {
      if (!task.lead_id) return;

      if (!map[task.lead_id]) {
        map[task.lead_id] = {
          overdue: 0,
          active: 0,
        };
      }

      if (task.bucket === "overdue") {
        map[task.lead_id].overdue += 1;
        return;
      }

      // Today / upcoming / other non-completed active tasks
      if (
        task.status !== "completed" &&
        task.status !== "cancelled"
      ) {
        map[task.lead_id].active += 1;
      }
    });

    return map;
  }, [tasks]);

  const baseColumns = [
    { key: "no", title: "No", dataIndex: "no", width: 60 },
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
      width: 220,
      render: (_, record) => {
        const taskInfo = taskIndicatorMap[record.id] || {
          overdue: 0,
          active: 0,
        };

        const hasTaskIndicator =
          taskInfo.overdue > 0 || taskInfo.active > 0;

        return (
          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: "48px",
            }}
          >
            <span
              style={{
                fontWeight: 500,
                color: "#1f1f1f",
              }}
            >
              {record.name}
            </span>

            {hasTaskIndicator && (
              <Space
                size={4}
                wrap
                style={{
                  marginTop: 4,
                }}
              >
                {taskInfo.overdue > 0 && (
                  <Tag
                    color="red"
                    style={{
                      margin: 0,
                      fontSize: 11,
                      lineHeight: "18px",
                    }}
                  >
                    {taskInfo.overdue} Overdue
                  </Tag>
                )}

                {taskInfo.active > 0 && (
                  <Tag
                    color="blue"
                    style={{
                      margin: 0,
                      fontSize: 11,
                      lineHeight: "18px",
                    }}
                  >
                    {taskInfo.active} Active Task
                    {taskInfo.active > 1 ? "s" : ""}
                  </Tag>
                )}
              </Space>
            )}
          </div>
        );
      },
    },
    {
      key: "phone",
      title: "Phone",
      dataIndex: "phone",
      width: 160,
      className: "no-row-navigation",
      render: (phone, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <a
            href={`tel:${resolveDialableNumber(phone, record.country)}`}
            onClick={(e) => e.stopPropagation()}
          >
            {phone ? resolveDialableNumber(phone, record.country) : "-"}
          </a>
          {phone && (
            <WhatsAppOutlined
              style={{ color: "#25D366", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                window.open(`https://wa.me/${resolveWhatsAppNumber(phone, record.country)}`);
              }}
            />
          )}
        </div>
      ),
    },
    // {
    //   title: "Email",
    //   dataIndex: "email",
    //   width: 220,
    //   render: (email) => (
    //     <div
    //       style={{
    //         whiteSpace: "nowrap",
    //         overflow: "hidden",
    //         textOverflow: "ellipsis",
    //       }}
    //       title={email}
    //     >
    //       {email}
    //     </div>
    //   ),
    // },
    {
      key: "assigned_to",
      title: "Agent",
      dataIndex: "assigned_to",
      width: 180,
      ellipsis: true,
      render: (_, record) => {
        const isOwnLead = record.assigned_to === authInfo?.userInfo?.user_id;

        // Self-service: a non-admin can reassign a lead currently assigned
        // to themselves, restricted to their own team (the backend enforces
        // this independently -- this list just keeps the picker from ever
        // offering a target it would reject).
        if (!isAdmin && isOwnLead && myTeammates.length) {
          return (
            <div style={{ maxWidth: "220px", width: "100%" }}>
              <Select
                placeholder="Reassign"
                style={{ width: "100%" }}
                size="small"
                value={record.assigned_to || undefined}
                onChange={(val) => assignLeadHandler(record.id, val)}
                onClick={(e) => e.stopPropagation()}
                options={[
                  { label: record.agent_name, value: record.assigned_to },
                  ...myTeammates.map((u) => ({ label: u.full_name, value: u.id })),
                ]}
              />
            </div>
          );
        }

        if (!isAdmin) {
          return (
            <div
              style={{
                maxWidth: "220px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
              title={record.agent_name}
            >
              {record.agent_name || "-"}
            </div>
          );
        }

        return (
          <div style={{ maxWidth: "220px", width: "100%" }}>
            <Select
              placeholder="Assign"
              style={{ width: "100%" }}
              size="small"
              value={record.assigned_to || undefined}
              onChange={(val) => assignLeadHandler(record.id, val)}
              onClick={(e) => e.stopPropagation()}
              options={(users || []).map((u) => ({
                label: u.full_name,
                value: u.id,
              }))}
            />
          </div>
        );
      },
    },
    {
      key: "source",
      title: "Source",
      dataIndex: "source",
      width: 180,
      ellipsis: true,
      render: (v) => {
        const text =
          v === "inquiry"
            ? "Inquiry"
            : v === "account"
            ? "Manual"
            : v || "-";

        return (
          <Tooltip title={text}>
            <Tag
              style={{
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {text}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      key: "purpose",
      title: "Purpose",
      dataIndex: "purpose",
      width: 180,
      ellipsis: true,
      render: (v) => {
        const text = v || "-";

        return (
          <Tooltip title={text}>
            <Tag
              style={{
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {text}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      width: 180,
      ellipsis: true,
      render: (_, record) => (
        <Tooltip title={record.status?.name}>
          <Tag
            color={record.status?.color || "default"}
            style={{
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {record.status?.name || "-"}
          </Tag>
        </Tooltip>
      ),
    },
    {
      key: "last_updated",
      title: "Last Updated",
      dataIndex: "last_updated",
      width: 120,
      render: (v) => (v ? formatToCompanyTimezone(v) : "-"),
    },
    {
      key: "date",
      title: "Created Date",
      dataIndex: "date",
      width: 120,
       render: (v) => {       
        return formatToCompanyTimezone(v);
      },
    },
  ];

  // Every custom field gets a column definition here -- "Show in list"
  // (set in CRM Customize) only decides what's visible by default; the
  // Show/Hide Columns panel is what actually controls whether a column
  // renders, same as every built-in column, so fields not flagged
  // show_in_list still need to be toggleable on demand.
  const customColumns = customFields
    .map((f) => ({
      key: `custom_${f.field_key}`,
      title: f.field_name,
      dataIndex: ["custom_fields", f.field_key],
      width: 180,
      ellipsis: true,
      render: (v) => Array.isArray(v) ? v.join(", ") : v || "-",
    }));

  const actionColumn = [
    {
      key: "actions",
      title: "Actions",
      fixed: "right",
      width: 130,

      render: (_, record) => {
        const canEditLead =
          isAdmin || record.assigned_to === authInfo?.userInfo?.user_id;
        const menuItems = [         
          {
            key: "task",
            icon: <CalendarOutlined />,
            label: "Add Task",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              setSelectedLead(record);
              setTaskDrawer(true);
            },
          },

          {
            key: "deals",
            icon: <SwapOutlined />,
            label: "Convert To Deal",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              // navigate(`/crm/deals?lead=${record.id}`);
              setSelectedLead(record);
              setConvertModal(true);
            },
          },

          {
            key: "whatsapp",
            icon: <WhatsAppOutlined />,
            label: "WhatsApp",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              window.open(`https://wa.me/${resolveWhatsAppNumber(record.phone, record.country)}`);
            },
          },

          ...(canEditLead
            ? [
                {
                  key: "edit",
                  icon: <EditOutlined />,
                  label: "Edit Lead",
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    setSelectedLead(record);
                    setEditDrawer(true);
                  },
                },
              ]
            : []),

          ...(isAdmin
            ? [
                {
                  key: "delete",
                  icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
                  label: "Delete Lead",
                  danger: true,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleDeleteLead(record);
                  },
                },
              ]
            : []),
        ];

        return (
          <Space>
            {/* VIEW */}
            {/* <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/crm/leads/${record.id}`);
              }}
            /> */}

            {/* CONVERT */}
            {/* <Button
              type="text"
              style={{
                color: "#16a34a",
                fontWeight: 600,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedLead(record);
                setConvertModal(true);
              }}
            >
              ⚡
            </Button> */}            

            {/* CALL */}
            <Tooltip title="Call The Lead ">
            <Button
              type="text"
              icon={<PhoneOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = `tel:${resolveDialableNumber(record.phone, record.country)}`;
              }}
            />
            </Tooltip>
            {/* ACTIVITY */}
            <ActivityPreviewPopover record={record} onViewActivity={onViewActivity}>
              <Button
                type="text"
                icon={<MessageOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewActivity?.(record);
                }}
              />
            </ActivityPreviewPopover>

            {/* MORE */}
            <Dropdown
              trigger={["click"]}
              menu={{
                items: menuItems.map((m) => ({
                  key: m.key,
                  icon: m.icon,
                  label: m.label,
                  danger: m.danger,
                  onClick: m.onClick,
                })),
              }}
            >
              <Button
                type="text"
                icon={<MoreOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const allColumns = [...baseColumns, ...customColumns, ...actionColumn];

  const columns = allColumns.filter((col) =>
    visibleColumnKeys.includes(col.key)
  );  

  return (
    <>
      {selectedRowKeys.length > 0 && (
        <div
          className="no-row-navigation"
          style={{
            display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10,
            marginBottom: 12, padding: "8px 12px",
            background: "#f0f5ff", border: "1px solid #adc6ff", borderRadius: 8,
          }}
        >
          <span style={{ fontWeight: 500 }}>{selectedRowKeys.length} selected</span>
          <Button size="small" onClick={() => setBulkReassignOpen(true)}>Reassign</Button>
          <Button size="small" onClick={() => setBulkStatusOpen(true)}>Change Status</Button>
          <Button size="small" onClick={() => setBulkFieldsOpen(true)}>Edit Fields</Button>
          <Button size="small" type="text" onClick={() => setSelectedRowKeys([])}>
            Clear selection
          </Button>
        </div>
      )}

      <Table
        rowKey="id"
        rowClassName={() => "clickable-row"}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e.target.closest(".no-row-navigation")) {
              return;
            }

            navigate(`/crm/leads/${record.id}`);
          },
        })}
        dataSource={list}
        columns={columns}
        loading={loading}
        bordered
        scroll={{ x: 1200 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: list.length,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} leads`,
          onChange: (page, pageSize) => {
            setPagination({
              current: page,
              pageSize,
            });
          },
        }}
      />

      {/* Convert Modal */}
      <Modal
        open={convertModal}
        onCancel={() => !convertLoading && setConvertModal(false)}
        title={`Convert Lead - ${selectedLead?.name || ""}`}
        footer={null}
        destroyOnClose
      >
        <Input
          placeholder="Deal Value"
          value={dealValue}
          onChange={(e) => setDealValue(e.target.value)}
          style={{ marginBottom: 10 }}
          disabled={convertLoading}
        />

        <DatePicker
          style={{ width: "100%", marginBottom: 10 }}
          onChange={(date) => setFollowUpDate(date)}
          disabled={convertLoading}
        />

        <Button 
          type="primary" 
          block 
          onClick={handleConvertSubmit}
          loading={convertLoading} 
          disabled={convertLoading} 
        >
          Convert
        </Button>
      </Modal>

      {/* Bulk Reassign */}
      <Modal
        title={`Reassign ${selectedRowKeys.length} lead${selectedRowKeys.length === 1 ? "" : "s"}`}
        open={bulkReassignOpen}
        onCancel={() => setBulkReassignOpen(false)}
        onOk={() => runBulkUpdate({ assigned_to: bulkValue })}
        okButtonProps={{ disabled: !bulkValue }}
        confirmLoading={bulkLoading}
        destroyOnClose
      >
        <Select
          style={{ width: "100%" }}
          placeholder="Select agent"
          value={bulkValue}
          onChange={setBulkValue}
          options={bulkReassignOptions}
          showSearch
          optionFilterProp="label"
        />
        {!isAdmin && (
          <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
            Only leads currently assigned to you, reassigned to a teammate,
            will go through -- others will be skipped.
          </div>
        )}
      </Modal>

      {/* Bulk Change Status */}
      <Modal
        title={`Change status for ${selectedRowKeys.length} lead${selectedRowKeys.length === 1 ? "" : "s"}`}
        open={bulkStatusOpen}
        onCancel={() => setBulkStatusOpen(false)}
        onOk={() => runBulkUpdate({ status_id: bulkValue })}
        okButtonProps={{ disabled: !bulkValue }}
        confirmLoading={bulkLoading}
        destroyOnClose
      >
        <LeadStatusSelect value={bulkValue} onChange={setBulkValue} />
      </Modal>

      {/* Bulk Edit Fields */}
      <Modal
        title={`Edit fields for ${selectedRowKeys.length} lead${selectedRowKeys.length === 1 ? "" : "s"}`}
        open={bulkFieldsOpen}
        onCancel={() => setBulkFieldsOpen(false)}
        onOk={() => {
          const payload = {};
          if (bulkFields.source_id) payload.source_id = bulkFields.source_id;
          if (bulkFields.purpose_id) payload.purpose_id = bulkFields.purpose_id;
          runBulkUpdate(payload);
        }}
        okButtonProps={{ disabled: !bulkFields.source_id && !bulkFields.purpose_id }}
        confirmLoading={bulkLoading}
        destroyOnClose
      >
        <div style={{ marginBottom: 6, color: "#888" }}>Source</div>
        <Select
          style={{ width: "100%", marginBottom: 12 }}
          placeholder="Leave blank to skip"
          allowClear
          value={bulkFields.source_id}
          onChange={(v) => setBulkFields((p) => ({ ...p, source_id: v }))}
          options={(sourceList || []).map((s) => ({ label: s.name, value: s.id }))}
        />
        <div style={{ marginBottom: 6, color: "#888" }}>Purpose</div>
        <Select
          style={{ width: "100%" }}
          placeholder="Leave blank to skip"
          allowClear
          value={bulkFields.purpose_id}
          onChange={(v) => setBulkFields((p) => ({ ...p, purpose_id: v }))}
          options={(purposeList || []).map((p) => ({ label: p.name, value: p.id }))}
        />
      </Modal>

      {/* Task Drawer */}
      <TaskDrawer
        open={taskDrawer}
        onClose={() => setTaskDrawer(false)}
        defaultLeadId={selectedLead?.id}
        leads={list}
        deals={deals}
        onSuccess={() => {
          setTaskDrawer(false);
        }}
      />
    </>
  );
};

export default LeadTable;
